/**
 * One snap, ready to draw: the sim resolves it, the client keeps what the field needs. The client
 * picks the seed and passes it in; every outcome comes from packages/sim.
 */
import {
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  createRng,
  formatPlayLog,
  playLogEntries,
  resolvePlay,
  DIFFICULTIES,
  type Difficulty,
  spotName,
} from '@tdmons/sim';
import type {
  Combatant,
  GameState,
  LogEntry,
  PlayEvent,
  PlayOutcome,
  TypeName,
  UnitId,
} from '@tdmons/sim';
import { PLAY1_SEED, play1Call, play1State } from '@tdmons/sim/fixtures/sample-drive';

export interface UnitView {
  unit: UnitId;
  /** Which sprite sheet draws it. */
  creatureId: string;
  name: string;
  types: readonly TypeName[];
  side: 'off' | 'def';
}

export interface PlayView {
  seed: number;
  title: string;
  before: GameState;
  outcome: PlayOutcome;
  events: readonly PlayEvent[];
  units: readonly UnitView[];
  /** formatPlayLog's lines, verbatim; the last one is the result. */
  log: readonly string[];
  /** The same lines, each tied to the event it describes (the result to the whistle). */
  entries: readonly LogEntry[];
  /** "1st & 10 at your 25". */
  situation: string;
}

/** What the field reads of a view: a snap to animate, or (slice 8b) a formation to stand in. */
export type FieldView = Pick<PlayView, 'title' | 'before' | 'events' | 'units'>;

const ORDINAL = ['', '1st', '2nd', '3rd', '4th'];

function unitView(unit: UnitId, c: Combatant, side: 'off' | 'def'): UnitView {
  return { unit, creatureId: c.creatureId, name: c.name, types: c.types, side };
}

/** The fourteen on the field, offense first, as the sprites draw them. */
export function unitsOf(before: GameState): UnitView[] {
  return [
    ...OFFENSE_SLOTS.map((s) => unitView(`off:${s}`, before.offense.lineup[s], 'off')),
    ...DEFENSE_SLOTS.map((s) => unitView(`def:${s}`, before.defense.lineup[s], 'def')),
  ];
}

export function viewOf(
  seed: number,
  before: GameState,
  outcome: PlayOutcome,
  title = `${play1Call.offense.name} vs ${play1Call.defense.name}`,
): PlayView {
  const units = unitsOf(before);
  return {
    seed,
    title,
    before,
    outcome,
    events: outcome.events,
    units,
    log: formatPlayLog(outcome, before),
    entries: playLogEntries(outcome, before),
    situation: `${ORDINAL[before.down] ?? `${before.down}th`} & ${before.toGo} at ${spotName(before.ballOn)}`,
  };
}

/** Sample-drive Play 1 on a seed: resolvePlay(play1State(), play1Call, createRng(seed)). */
export function play1View(seed: number = PLAY1_SEED): PlayView {
  const before = play1State();
  return viewOf(seed, before, resolvePlay(before, play1Call, createRng(seed)));
}

/** A seed typed by a person: a whole number the rng accepts (0 to 2^32 - 1), or null. */
export function parseSeed(raw: string | null): number | null {
  const s = raw?.trim() ?? '';
  if (!/^\d{1,10}$/.test(s)) return null;
  const n = Number(s);
  return n <= 0xffffffff ? n : null;
}

/** `?seed=` from the URL, if it is a seed. */
export const seedFromQuery = (search: string): number | null =>
  parseSeed(new URLSearchParams(search).get('seed'));

/** `?coach=` from the URL: the AI's level, if it names one. */
export function coachFromQuery(search: string): Difficulty | null {
  const c = new URLSearchParams(search).get('coach');
  return (DIFFICULTIES as readonly string[]).includes(c ?? '') ? (c as Difficulty) : null;
}
